<template>
  <div class="auth-page">
    <!-- Background Video -->
    <div class="auth-background" :class="{ 'fallback-active': videoFailed }">
      <video
        v-if="!videoFailed"
        autoplay
        muted
        loop
        playsinline
        class="background-video"
        @error="onVideoError"
      >
  <source :src="videoSource" type="video/mp4">
      </video>
      <div class="video-overlay"></div>
    </div>

    <!-- Main Content -->
    <div class="auth-content">
      <v-container fluid class="auth-container">
        <v-row align="center" justify="center" class="min-height-screen">
          <v-col cols="12" sm="8" md="6" lg="4" xl="3">
            <!-- Language Switcher (Hidden on mobile - use header switcher) -->
            <div class="text-end mb-4 d-none d-sm-block">
              <LanguageSwitcher />
            </div>

            <!-- Auth Card -->
            <v-card 
              class="auth-card glass-effect"
              elevation="24"
            >
              <!-- Header -->
              <v-card-title class="auth-header text-center pa-6">
                <div class="auth-logo mb-4">
                  <v-img
                    :src="logoSrc"
                    alt="Korkem Auto"
                    :width="84"
                    :height="84"
                    cover
                    rounded="circle"
                    class="mx-auto auth-logo-img"
                  />
                </div>
                <h2 class="text-h4 text-gradient">
                  {{ $t('auth.login') }}
                </h2>
                <p class="text-subtitle-1 mt-2 text-medium-emphasis">
                  {{ $t('common.welcome') }}
                </p>
              </v-card-title>

              <!-- Form -->
              <v-card-text class="pa-6 pt-0">
                <v-form 
                  ref="loginForm"
                  v-model="formValid"
                  @submit.prevent="handleLogin"
                >
                  <!-- Phone Input -->
                  <PhoneInput
                    v-model="credentials.phone"
                    :label="$t('auth.phone')"
                    :rules="phoneRules"
                    :error-messages="fieldErrors.phone"
                    @update:model-value="clearFieldError('phone')"
                  />

                  <!-- Password Input -->
                  <v-text-field
                    v-model="credentials.password"
                    :label="$t('auth.password')"
                    :rules="passwordRules"
                    :error-messages="fieldErrors.password"
                    :type="showPassword ? 'text' : 'password'"
                    variant="outlined"
                    prepend-inner-icon="mdi-lock"
                    :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    class="mb-4"
                    name="password"
                    autocomplete="current-password"
                    autocapitalize="none"
                    autocorrect="off"
                    spellcheck="false"
                    @click:append-inner="showPassword = !showPassword"
                    @input="clearFieldError('password')"
                  />

                  <!-- Remember Me -->
                  <div class="remember-forgot-section mb-6">
                    <v-checkbox
                      v-model="rememberMe"
                      :label="$t('auth.rememberMe')"
                      density="compact"
                      hide-details
                      class="remember-checkbox"
                    />
                    
                    <v-btn
                      variant="text"
                      color="primary"
                      size="small"
                      class="forgot-password-btn"
                      @click="$router.push('/forgot-password')"
                    >
                      {{ $t('auth.forgotPassword') }}
                    </v-btn>
                  </div>

                  <!-- Error Alert -->
                  <v-alert
                    v-if="error"
                    type="error"
                    variant="tonal"
                    class="mb-4"
                    closable
                    @click:close="clearError"
                  >
                    {{ error }}
                  </v-alert>

                  <!-- Submit Button -->
                  <v-btn
                    type="submit"
                    block
                    size="large"
                    class="btn-primary mb-4"
                    :loading="loading"
                    :disabled="!formValid"
                  >
                    <v-icon start>mdi-login</v-icon>
                    {{ $t('auth.loginButton') }}
                  </v-btn>

                  <!-- Divider -->
                  <v-divider class="auth-divider">
                    <span class="text-medium-emphasis divider-text">{{ $t('common.or') }}</span>
                  </v-divider>

                  <!-- Register Link -->
                  <div class="register-link-section text-center">
                    <span class="text-medium-emphasis register-text">
                      {{ $t('auth.noAccount') }}
                    </span>
                    <v-btn
                      variant="text"
                      color="primary"
                      class="register-btn"
                      @click="$router.push('/register')"
                    >
                      {{ $t('auth.registerHere') }}
                    </v-btn>
                  </div>
                </v-form>
              </v-card-text>
            </v-card>

            <!-- Footer Links -->
            <div class="auth-footer text-center mt-6">
              <v-btn
                variant="text"
                size="small"
                @click="$router.push('/')"
              >
                <v-icon start>mdi-home</v-icon>
                {{ $t('nav.home') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import PhoneInput from '@/components/ui/PhoneInput.vue'
import { withBasePath } from '@/utils/path'

// Composables
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()

// Refs
const loginForm = ref(null)
const formValid = ref(false)
const showPassword = ref(false)
const rememberMe = ref(false)
const fieldErrors = ref({})

const credentials = ref({
  phone: '',
  password: ''
})

// Background video fallback handling
const videoFailed = ref(false)
const onVideoError = () => {
  videoFailed.value = true
}

const videoSource = withBasePath('/videos/hero-video-1.mp4')
const logoSrc = withBasePath('/logo.svg')

// Computed
const loading = computed(() => authStore.loading)
const error = computed(() => {
  const rawError = authStore.error
  if (!rawError) return null
  
  // Translate known error codes
  const errorTranslations = {
    'ACCOUNT_PENDING': t('auth.loginForm.accountPending'),
    'ACCOUNT_BLOCKED': t('auth.loginForm.accountBlocked')
  }
  
  return errorTranslations[rawError] || rawError
})

// Validation rules
const phoneRules = [
  v => !!v || t('validation.required'),
  v => {
    const digits = (v || '').replace(/\D/g, '')
    // Строго требуем +7 и ровно 11 цифр начиная с 7
    return (digits.length === 11 && digits.startsWith('7') && v.startsWith('+7')) || t('validation.phone')
  }
]

const passwordRules = [
  v => !!v || t('validation.required'),
  v => v.length >= 6 || t('validation.minLength', { min: 6 })
]

// Methods
const handleLogin = async () => {
  if (!formValid.value) return
  // Қос жіберуден қорғау (Enter + тап, немесе жылдам екі рет басу)
  if (loading.value) return

  try {
    // Normalize phone number before sending - same logic as other auth forms
    const originalPhone = credentials.value.phone
    let normalizedPhone = originalPhone.replace(/\D/g, '')

    if (normalizedPhone.startsWith('8') && normalizedPhone.length === 11) {
      normalizedPhone = '7' + normalizedPhone.slice(1)
    }

    // Add + prefix for backend consistency
    const phoneWithPrefix = `+${normalizedPhone}`

    const payload = {
      phone: phoneWithPrefix,
      // Пернетақта/autofill қосқан бос орындарды алып тастаймыз (iOS-та жиі кездеседі)
      password: (credentials.value.password || '').trim()
    }
    
    // Console log removed
    await authStore.login(payload, rememberMe.value)
    
    // Console log removed
    
    // Redirect to last visited route or appropriate dashboard
    const redirectPath = route.query.redirect || authStore.getRedirectRoute()
    // Console log removed
    
    router.push(redirectPath)
    
  } catch (err) {
    // Console log removed
    // Console log removed
    
    // Handle specific field errors
    if (err.response?.data?.fieldErrors) {
      fieldErrors.value = err.response.data.fieldErrors
      // Console log removed
    }
  }
}

const clearError = () => {
  authStore.clearError()
  fieldErrors.value = {}
}

const clearFieldError = (field) => {
  if (fieldErrors.value[field]) {
    delete fieldErrors.value[field]
  }
}

// Auto-format phone number
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')
  
  // Format as +7 (XXX) XXX-XX-XX
  if (digits.length >= 1) {
    let formatted = '+7'
    if (digits.length > 1) {
      formatted += ` (${digits.slice(1, 4)}`
      if (digits.length > 4) {
        formatted += `) ${digits.slice(4, 7)}`
        if (digits.length > 7) {
          formatted += `-${digits.slice(7, 9)}`
          if (digits.length > 9) {
            formatted += `-${digits.slice(9, 11)}`
          }
        }
      }
    }
    return formatted
  }
  return value
}

// Lifecycle
onMounted(() => {
  // Clear any previous errors
  authStore.clearError()
})
</script>

<style scoped>
.auth-page {
  position: relative;
  min-height: 100%;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.auth-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.background-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
}

/* Fallback: static gradient background when video fails */
.auth-background.fallback-active {
  background: linear-gradient(135deg, #0d47a1 0%, #1976d2 50%, #42a5f5 100%);
}

.auth-content {
  position: relative;
  z-index: 1;
  width: 100%;
}

.auth-container {
  min-height: 100%;
  min-height: 100dvh;
  padding: 2rem 1rem;
}

.min-height-screen {
  min-height: 100%;
  min-height: 100dvh;
}

.auth-card {
  border-radius: 24px;
  border: 1px solid rgba(255, 193, 7, 0.3);
  animation: slideInUp 0.6s ease-out;
}

.auth-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.auth-logo img {
  filter: drop-shadow(0 2px 8px rgba(255, 193, 7, 0.3));
}

/* Circular logo badge — hides the square white background of the raster logo */
.auth-logo-img {
  background: #fff;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.85);
}

.auth-footer {
  opacity: 0.8;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Input field custom styles */
:deep(.v-field) {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
}

:deep(.v-field--focused) {
  background: rgba(255, 255, 255, 0.08);
}

:deep(.v-text-field .v-field__input) {
  font-size: 1rem;
}

/* Button styles */
.btn-primary {
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
}

/* Divider styles */
.auth-divider {
  margin: 1.5rem 0;
}

.divider-text {
  padding: 0 1rem;
  font-size: 0.9rem;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .auth-container {
    padding: 0.5rem;
    min-height: 100%;
    min-height: 100dvh;
  }
  
  .min-height-screen {
    min-height: 100%;
    min-height: 100dvh;
  }
  
  .auth-card {
    margin: 0;
    border-radius: 20px;
    max-width: 100%;
    width: 100%;
  }
  
  /* Language switcher hidden on mobile - removes unnecessary margin */
  .text-end.mb-4.d-none.d-sm-block {
    display: none !important;
  }
  
  .auth-header {
    padding: 1rem 0.75rem;
  }
  
  .v-card-text {
    padding: 1rem 0.75rem;
  }
  
  /* Улучшенная адаптация для небольших экранов */
  .text-h4 {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .text-subtitle-1 {
    font-size: 0.9rem !important;
    line-height: 1.4 !important;
  }
  
  /* Mobile input improvements */
  :deep(.v-text-field input) {
    font-size: 16px !important; /* Prevent zoom on iOS */
    -webkit-appearance: none;
    appearance: none;
  }
  
  :deep(.v-text-field .v-field__input) {
    min-height: 48px; /* Better touch target */
  }
}

/* Remember Me and Forgot Password responsive layout */
.remember-forgot-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

@media (max-width: 480px) {
  .auth-container {
    padding: 0.5rem 0.25rem;
  }
  
  .text-h4 {
    font-size: 1.3rem !important;
    margin-bottom: 0.25rem !important;
    line-height: 1.2 !important;
  }
  
  .text-subtitle-1 {
    font-size: 0.8rem !important;
    line-height: 1.25 !important;
    margin-top: 0.5rem !important;
  }
  
  .auth-logo img {
    max-width: 45px;
  }
  
  /* Extra mobile optimizations */
  .auth-card {
    border-radius: 16px;
    margin: 0;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3) !important;
  }
  
  .auth-header {
    padding: 0.75rem 0.5rem 0.5rem;
  }
  
  .v-card-text {
    padding: 0.5rem 0.5rem 1rem;
  }
  
  /* Remember Me and Forgot Password on mobile */
  .remember-forgot-section {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .remember-checkbox {
    align-self: flex-start;
  }
  
  .forgot-password-btn {
    align-self: center;
    padding: 0.5rem 1rem !important;
    min-height: 36px !important;
    font-size: 0.85rem !important;
  }
  
  :deep(.v-btn) {
    min-height: 48px;
    font-size: 0.9rem !important;
  }
  
  /* Prevent viewport zoom on input focus */
  :deep(input[type="tel"]) {
    font-size: 16px !important;
  }
  
  :deep(input[type="password"]) {
    font-size: 16px !important;
  }
  
  /* Input field mobile improvements */
  :deep(.v-text-field .v-field) {
    margin-bottom: 0.5rem;
  }
  
  /* Checkbox label mobile optimization */
  :deep(.v-checkbox .v-label) {
    font-size: 0.85rem !important;
    line-height: 1.2 !important;
  }
  
  /* Divider mobile adaptation */
  .auth-divider {
    margin: 1rem 0;
  }
  
  .divider-text {
    font-size: 0.8rem;
    padding: 0 0.75rem;
  }
  
  /* Register link mobile optimization */
  .register-link-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
  
  .register-text {
    font-size: 0.85rem !important;
    line-height: 1.3 !important;
  }
  
  .register-btn {
    padding: 0.25rem 0.75rem !important;
    min-height: 32px !important;
    font-size: 0.85rem !important;
    margin: 0 !important;
  }
}

/* Extra small screens (< 360px) */
@media (max-width: 360px) {
  .auth-container {
    padding: 0.25rem 0.125rem;
  }
  
  .text-h4 {
    font-size: 1.2rem !important;
  }
  
  .text-subtitle-1 {
    font-size: 0.75rem !important;
  }
  
  .auth-header {
    padding: 0.5rem 0.25rem;
  }
  
  .v-card-text {
    padding: 0.5rem 0.25rem 0.75rem;
  }
  
  :deep(.v-text-field) {
    margin-bottom: 0.75rem !important;
  }
  
  .remember-forgot-section {
    margin-bottom: 1rem !important;
  }
  
  .auth-divider {
    margin: 0.75rem 0 !important;
  }
}

</style>
